import type { PanInfo } from "motion";

import { useCallback, useMemo, useRef, useState, type PropsWithChildren } from "react";

import { DragContext } from "./DragContext";

export type DragSource = { height: number; width: number; top: number; left: number; column: number; row: number };
export type DragDestination = { column: number; row: number };
export type DragMoveEvent = { from: DragSource; to: DragDestination };

const DRAG_MOVE_THROTTLE_MS = 1000 / 30;

export function DragProvider({
  onMove,
  children,
}: PropsWithChildren<{
  onMove: (move: DragMoveEvent) => void;
}>) {
  const lastMoveAtRef = useRef(0);
  const [dragSource, setDragSource] = useState<DragSource | null>(null);
  const [dragPreview, setDragPreview] = useState<DragDestination | null>(null);

  const handleDragEnd = useCallback(() => {
    setDragSource(null);
    setDragPreview(null);
    if (dragSource && dragPreview) onMove({ from: dragSource, to: dragPreview });
  }, [onMove, dragSource, dragPreview]);

  const handleDragStart = useCallback((event: MouseEvent | TouchEvent | PointerEvent) => {
    if (!event.target) return;
    const itemElement: HTMLElement = (event.target as HTMLElement).closest("[data-drag-item]")!;
    if (!itemElement) return;
    const columnElement: HTMLElement = itemElement.closest("[data-drag-column]")!;
    if (!columnElement) return;
    const row = Number.parseInt(itemElement.dataset.dragItem!, 10);
    const column = Number.parseInt(columnElement.dataset.dragColumn!, 10);
    if (Number.isNaN(row)) throw new TypeError("data-drag-item must be a valid integer");
    if (Number.isNaN(column)) throw new TypeError("data-drag-column must be a valid integer");
    lastMoveAtRef.current = 0;
    const colBox = columnElement.getBoundingClientRect();
    const { height, width, top, left } = itemElement.getBoundingClientRect();
    setDragSource({ height, width, top: top - colBox.top, left: left - colBox.left, column, row });
    setDragPreview({ column, row });
  }, []);

  const handleDragMove = useCallback(
    (event: MouseEvent | TouchEvent | PointerEvent, { point: { x, y } }: PanInfo) => {
      if (!dragSource) return;
      const now = performance.now();
      if (now - lastMoveAtRef.current < DRAG_MOVE_THROTTLE_MS) return;
      lastMoveAtRef.current = now;
      const columnEl = document
        .elementsFromPoint(x, y)
        .find((element) => "dragColumn" in (element as HTMLElement).dataset) as HTMLElement | undefined;
      if (!columnEl) return;
      const column = Number.parseInt(columnEl.dataset.dragColumn!, 10);
      if (Number.isNaN(column)) throw new TypeError("data-drag-column must be a valid integer");
      const itemElements = columnEl.querySelectorAll(
        dragSource.column === column
          ? `[data-drag-item]:not([data-drag-item="${dragSource.row}"])`
          : `[data-drag-item]`,
      );
      const row = Array.from(itemElements).findIndex((itemEl) => {
        const { top, height } = itemEl.getBoundingClientRect();
        return y < top + height / 2;
      });
      setDragPreview({ column, row: row === -1 ? itemElements.length : row });
    },
    [dragSource],
  );

  const context = useMemo(
    () => ({ dragSource, dragPreview, handleDragEnd, handleDragMove, handleDragStart }),
    [dragSource, dragPreview, handleDragEnd, handleDragMove, handleDragStart],
  );
  return <DragContext.Provider value={context}>{children}</DragContext.Provider>;
}
